
import { namespace } from 'vuex-class';
import { Vue, Component, InjectReactive, Prop } from 'vue-property-decorator';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderStreamsComparatorBar from './ProviderStreamsComparatorBar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import { Provider, StreamInfo, CompetitionContent } from './types';
import { Platform, SportEventAggregation, SportType } from '@/@types/graphql-types';
import {
	GetCompetitionOffersDocument,
	GetCompetitionOffersQueryVariables,
	GetCompetitionOffersQuery,
} from '@/pages/graphql/queries/GetCompetitionOffers.query';

import { contextIs, SingleStepContext } from '@/helpers/sports/config';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { LocaleResponse } from '@/helpers/locale-helper';

const Language = namespace('language');

@Component({
	name: 'ProviderStreamsComparator',
	components: {
		HiddenHorizontalScrollbar,
		ProviderStreamsComparatorBar,
		ProviderIcon,
	},
	apollo: {
		competitionOffers: {
			query: GetCompetitionOffersDocument,
			variables(): GetCompetitionOffersQueryVariables {
				return this.queryVariables;
			},
			update(data: GetCompetitionOffersQuery) {
				// avoid cases where no providers are selected, this will trigger the setter in the getter
				this.selectedProvider = '';
				return data;
			},
		},
	},
})
export default class ProviderStreamsComparator extends Vue {
	/* Data Fetching */
	@Prop({ required: true }) context: SingleStepContext;
	// Required on competition pages
	@Prop({ default: null }) competitionId: string;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;

	get queryVariables(): GetCompetitionOffersQueryVariables {
		const startFrom = shiftDateWithTimezone(this.localeObject.timezone);

		const variables = {
			country: this.country,
			language: this.language,
			platform: Platform.Web,
			timezoneOffset: 0,
			filter: {
				startAt: {
					from: startFrom.toISOString(),
					to: shiftDateWithTimezone(this.localeObject.timezone, this.comparatorDays, startFrom).toISOString(),
				},
				packages: this.providers.map(({ shortName }) => shortName),
				sports: [this.context.sportType],
				competitionIds: [] as string[],
			},
			groupBy: SportEventAggregation.Competition,
			first: 7, // number of competitions to get (ideally it would be all the competitions in 7 days, max: ~65)
			bucketSize: 15, // number of offers per event, ideally should match the bucketSize in the competition page query
		};

		if (contextIs.competition(this.context)) {
			if (this.competitionId == null) {
				console.warn('[ProviderStreamsComparator]: Missing required prop: "competitionId"');
			} else {
				variables.filter.competitionIds = [this.competitionId];
			}
		}

		return variables;
	}

	/* Provider Selection */
	// This whole setup with a computed setter and a setter in the getter is because
	// we can't access the query data in the created hook, ideally it would be this:
	// selectedProvider = this.providerStreamsInfo[0].provider.shortName;

	private privateSelectedProvider = '';

	public get selectedProvider() {
		if (this.privateSelectedProvider === '') {
			this.selectedProvider = this.providerStreamsInfo[0].provider.shortName;
		}

		return this.privateSelectedProvider;
	}

	public set selectedProvider(shortName: string) {
		this.privateSelectedProvider = shortName;
	}

	/* Comparison Data */
	competitionOffers: GetCompetitionOffersQuery;
	@InjectReactive('sportProvidersByShortName') providers: Provider[];

	get providerStreamsInfo() {
		const competitions = this.competitionOffers?.sportEventBucketsV2.edges;
		if (competitions == null) return [];

		const result = {} as { [shortName: string]: StreamInfo };
		this.providers.forEach(
			provider => (result[provider.shortName] = { provider, totalCount: 0, competitions: {} })
		);

		competitions.forEach(({ key, node }) => {
			const events = node.edges;
			if (events == null || events.length === 0) return;

			const { name = '', iconUrl, iconBlurHash, fullPath } = key.competition?.content as CompetitionContent;
			const competitionData = { name, iconUrl, iconBlurHash, fullPath, count: 0 };

			events.forEach(event => {
				// same event might have multiple offers for whatever reason (highlight, main event, audio languages, etc.)
				const offerProviders = new Set(event.node.offers.map(offer => offer.package.shortName));
				offerProviders.forEach(shortName => {
					if (result[shortName] == null) return;

					const competitionItem = result[shortName].competitions;
					if (competitionItem[name] == null) {
						competitionItem[name] = { ...competitionData };
					}

					competitionItem[name].count++;
					result[shortName].totalCount++;
				});
			});
		});

		return Object.values(result)
			.filter(({ totalCount }) => totalCount > 0)
			.sort(({ totalCount: a }, { totalCount: b }) => b - a)
			.slice(0, 10);
	}

	get selectedProviderStreamInfo() {
		return this.providerStreamsInfo.find(({ provider }) => provider.shortName === this.selectedProvider);
	}

	get eventTotal() {
		return this.providerStreamsInfo.reduce((total, info) => (total += info.totalCount), 0);
	}

	get topProvider() {
		return {
			total: this.providerStreamsInfo[0]?.totalCount ?? 0,
			name: this.providerStreamsInfo[0]?.provider?.clearName ?? '',
		};
	}

	get comparatorDays() {
		return this.context.sportType === SportType.FormulaRacing ? 14 : 7;
	}

	get providerList() {
		const sorted = this.providerStreamsInfo.sort((a, b) => {
			return b.totalCount - a.totalCount;
		});

		return sorted.map((provider: any) => {
			return `${provider.provider.clearName} (${provider.totalCount})`;
		});
	}

	get formattedProviderList() {
		if (this.providerList.length > 1) {
			const last = this.providerList.pop();
			return `${this.providerList.join(', ')} and ${last}`;
		} else {
			return this.providerList;
		}
	}

	// TRANSLATIONS
	get eventTranslation() {
		if (this.context.sportType === 'FORMULA_RACING') {
			return (count: number) => this.$t('WEBAPP_GLOBAL_RACES', { count });
		}

		return (count: number) => this.$t('WEBAPP_GLOBAL_MATCHES', { count });
	}
}
